import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { FileUpload, PRESETS } from '@/features/files'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { graphqlToForm } from '@/features/translations'
import { useBranding } from '@/features/branding'
import Checkbox from '@/components/ui/Checkbox'

import useCategoryForm, {
  FormData,
  TRANSLATABLE_FIELDS
} from '../hooks/useCategoryForm'

type CreateProps = {
  layoutId: number
  onDone: () => void
  onCancel: () => void
}

type EditProps = {
  category: GraphQL.CategoryFragmentFragment
  onDone: () => void
  onCancel: () => void
}

type Props = CreateProps | EditProps

export default function CategoryForm(props: Props) {
  const { onDone, onCancel } = props
  const category = (props as EditProps).category ?? null
  const layoutId = (props as CreateProps).layoutId ?? null

  const {
    branding: { supportedLocales }
  } = useBranding()

  const { t } = useLingui()

  const { create, update, errors } = useCategoryForm({
    layoutId,
    id: category?.id,
    onDone
  })

  return (
    <Form.Root<FormData>
      onSubmit={category ? update : create}
      serverErrors={errors}
      defaultValues={{
        cover: getFragment(SharedAPI.file.FILE_FRAGMENT, category?.cover),
        name: category?.name ?? '',
        sortBy: category?.sortBy ?? undefined,
        translations: graphqlToForm(
          (category?.translations ?? []).flatMap(trans =>
            getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
          ),
          TRANSLATABLE_FIELDS,
          supportedLocales ?? []
        )
      }}
    >
      <Form.Translation translatableFields={TRANSLATABLE_FIELDS}>
        <div className="flex flex-col gap-7 p-2.5">
          <div className="flex flex-row items-center gap-6">
            <div>
              <Form.Field
                name="cover"
                rules={{
                  required: t`This field is required`
                }}
              >
                {({ renderProps: { onChange, ...props } }) => (
                  <FileUpload
                    placeholder={t`Upload Category Image`}
                    accept="image/*"
                    onChange={onChange}
                    imageResize={PRESETS.MEDIUM}
                    {...props}
                    className="size-40 aspect-auto"
                  />
                )}
              </Form.Field>
            </div>
            <Form.Field
              name="name"
              label={`${t`Name`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  {...props}
                />
              )}
            </Form.Field>
          </div>
          <Form.Field name="sortBy" label={t`Should sort alphabetically`}>
            {({ renderProps: { onChange, value, ...props } }) => (
              <Checkbox
                checked={value === GraphQL.CategorySortTypes.Alphabetically}
                onChange={() => {
                  const isSelected =
                    value === GraphQL.CategorySortTypes.Alphabetically

                  if (isSelected) onChange(null)
                  else onChange(GraphQL.CategorySortTypes.Alphabetically)
                }}
                {...props}
              >
                <Trans>
                  When this is selected the category will automatically always
                  sort it&apos;s goodies alphabetically and show a header for
                  each letter.
                </Trans>
              </Checkbox>
            )}
          </Form.Field>
          <footer className="flex flex-row items-center justify-end gap-4">
            <Button variant="outline" onClick={onCancel} type="button">
              <Trans>Cancel</Trans>
            </Button>
            <Form.Submit>
              <Trans>Save</Trans>
            </Form.Submit>
          </footer>
        </div>
      </Form.Translation>
    </Form.Root>
  )
}
